/**
 * IndexManager - Optimized Index System
 * 
 * Provides O(1) and O(log n) lookups for all queries
 * Replaces linear O(n) tree walking with hash-based and tree-based indices
 */

import * as Y from 'yjs';

/**
 * Node reference in index
 */
export interface NodeReference {
  element: Y.XmlElement;
  position: number;      // Position in document (for ordering)
  containerId: string;   // Which container (page, slide, etc.)
}

/**
 * Index statistics
 */
export interface IndexStats {
  byId: number;
  byClass: number;
  fullText: number;
  tags?: number;
  spatial?: number;
  buildTime?: number;
  totalWords?: number;
}

/**
 * IndexManager - High-performance index system
 */
export class IndexManager {
  // ========================================================================
  // CORE INDICES
  // ========================================================================
  
  // ID index - O(1) lookup
  private byId: Map<string, NodeReference> = new Map();
  
  // ClassName index - O(1) lookup
  private byClassName: Map<string, Set<string>> = new Map();
  
  // Tag index - O(1) lookup
  private byTag: Map<string, Set<string>> = new Map();
  
  // ========================================================================
  // FULL-TEXT INDEX (Inverted Index)
  // ========================================================================
  
  // Word → NodeIds that contain it
  private wordIndex: Map<string, Set<string>> = new Map();
  
  // NodeId → Words it contains (for incremental updates)
  private nodeWords: Map<string, Set<string>> = new Map();
  
  // ========================================================================
  // METADATA INDICES
  // ========================================================================
  
  // Position tracking
  private nodePositions: Map<string, number> = new Map();
  
  // Container tracking
  private nodeContainers: Map<string, string> = new Map();
  
  // ========================================================================
  // STATISTICS
  // ========================================================================
  
  private lastBuildTime: number = 0;
  
  // ========================================================================
  // BUILD INDICES
  // ========================================================================
  
  /**
   * Build all indices from Y.js document
   * 
   * @param ydoc - Y.js document
   * @returns Index statistics
   */
  async buildIndices(ydoc: Y.Doc): Promise<IndexStats> {
    const startTime = performance.now();
    
    console.log('[IndexManager] 🔍 Building indices from ydoc:', ydoc.guid);
    
    // Clear existing indices
    this.clear();
    
    // ✅ CRITICAL FIX: Try 'prosemirror' first (v2 standard), fallback to 'content'
    // v2 apps use 'prosemirror', some v1 apps use 'content'
    let fragment = ydoc.getXmlFragment('prosemirror');
    
    console.log('[IndexManager] 🔍 prosemirror fragment:', {
      length: fragment.length,
      firstChild: (fragment.get(0) as any)?.nodeName || 'none',
      toString: fragment.toString().substring(0, 100)
    });
    
    // Fallback to 'content' if prosemirror is empty
    if (!fragment || fragment.length === 0) {
      fragment = ydoc.getXmlFragment('content');
      console.log('[IndexManager] ⚠️ prosemirror empty, trying content fallback:', fragment.length);
    } else {
      console.log('[IndexManager] ✅ Using prosemirror fragment:', fragment.length, 'elements');
    }
    
    if (!fragment || fragment.length === 0) {
      console.warn('[IndexManager] ❌ No content fragment found or fragment is empty');
      return this.getStats();
    }
    
    // Walk tree and index all nodes
    let position = 0;
    position = this.walkAndIndex(fragment, position, 'root');
    
    const duration = performance.now() - startTime;
    this.lastBuildTime = duration;
    
    const stats = this.getStats();
    stats.buildTime = duration;
    
    console.log('[IndexManager] ✅ Indices built:', stats);
    
    return stats;
  }
  
  /**
   * Walk Y.js tree and build indices recursively
   */
  private walkAndIndex(
    element: Y.XmlElement | Y.XmlFragment,
    position: number,
    containerId: string
  ): number {
    let currentPos = position;
    
    console.log(`[IndexManager] 🔍 walkAndIndex called: {length: ${element.length}, containerId: '${containerId}'}`);
    
    for (let i = 0; i < element.length; i++) {
      const child = element.get(i);
      
      console.log(`[IndexManager] 🔍 Processing child ${i}:`, {
        type: child.constructor.name,
        isXmlElement: child instanceof Y.XmlElement,
        nodeName: (child as any).nodeName || 'N/A'
      });
      
      if (child instanceof Y.XmlElement) {
        const id = child.getAttribute('id');
        const className = child.getAttribute('className');
        const nodeName = child.nodeName;
        
        console.log(`[IndexManager] 📋 XmlElement found:`, {
          nodeName,
          hasId: !!id,
          id: id || 'NONE',
          hasClassName: !!className,
          className: className || 'NONE',
          childCount: child.length,
          hasChildren: child.length > 0,
          childrenCount: child.length
        });
        
        if (id) {
          console.log(`[IndexManager] ✅ Indexing element with ID: ${id}`);
          
          // INDEX BY ID
          this.byId.set(id, {
            element: child,
            position: currentPos,
            containerId
          });
          
          // INDEX POSITION
          this.nodePositions.set(id, currentPos);
          this.nodeContainers.set(id, containerId);
          
          // INDEX BY CLASSNAME
          const className = child.getAttribute('className');
          if (className) {
            if (!this.byClassName.has(className)) {
              this.byClassName.set(className, new Set());
            }
            this.byClassName.get(className)!.add(id);
          }
          
          // INDEX BY TAGS
          const tags = child.getAttribute('tags');
          if (Array.isArray(tags)) {
            tags.forEach(tag => {
              if (!this.byTag.has(tag)) {
                this.byTag.set(tag, new Set());
              }
              this.byTag.get(tag)!.add(id);
            });
          }
          
          // INDEX TEXT CONTENT
          const text = this.extractText(child);
          console.log(`[IndexManager] 📝 Text extracted from ${id}:`, {
            length: text.length,
            preview: text.substring(0, 50)
          });
          if (text && text.length > 0) {
            this.indexText(id, text);
          }
        } else {
          // ❌ CRITICAL ISSUE: Element has NO ID - skipping indexing!
          const text = this.extractText(child);
          console.warn(`[IndexManager] ⚠️ SKIPPING element with NO ID:`, {
            nodeName,
            hasClassName: !!className,
            className: className || 'NONE',
            textPreview: text.substring(0, 50),
            textLength: text.length,
            reason: 'Elements without IDs are NOT indexed!'
          });
        }
        
        // Recursively index children
        currentPos = this.walkAndIndex(child, currentPos, containerId);
        currentPos++;
      } else {
        console.log(`[IndexManager] ⏭️ Skipping non-XmlElement child ${i}`);
      }
    }
    
    console.log(`[IndexManager] ✅ walkAndIndex complete: {processed: ${element.length}, currentPos: ${currentPos}}`);
    return currentPos;
  }
  
  /**
   * Index text content using inverted index
   */
  private indexText(nodeId: string, text: string): void {
    // Tokenize text into words
    const words = text
      .toLowerCase()
      .replace(/[^\w\s]/g, ' ') // Remove punctuation
      .split(/\s+/)
      .filter(w => w.length > 2); // Skip very short words
    
    const uniqueWords = new Set(words);
    
    // Build word → nodeIds index (inverted index)
    uniqueWords.forEach(word => {
      if (!this.wordIndex.has(word)) {
        this.wordIndex.set(word, new Set());
      }
      this.wordIndex.get(word)!.add(nodeId);
    });
    
    // Build nodeId → words index (for updates/removal)
    this.nodeWords.set(nodeId, uniqueWords);
  }
  
  /**
   * Extract all text from Y.XmlElement recursively
   */
  private extractText(element: Y.XmlElement): string {
    let text = '';
    
    for (let i = 0; i < element.length; i++) {
      const child = element.get(i);
      
      if (child instanceof Y.XmlText) {
        text += child.toString();
      } else if (child instanceof Y.XmlElement) {
        text += ' ' + this.extractText(child);
      }
    }
    
    return text.trim();
  }
  
  // ========================================================================
  // QUERY METHODS (O(1) or O(log n))
  // ========================================================================
  
  /**
   * Get node by ID - O(1)
   */
  getById(nodeId: string): NodeReference | null {
    return this.byId.get(nodeId) || null;
  }
  
  /**
   * Get all node IDs with className - O(1)
   */
  getByClassName(className: string): Set<string> {
    return this.byClassName.get(className) || new Set();
  }
  
  /**
   * Get all node IDs with tag - O(1)
   */
  getByTag(tag: string): Set<string> {
    return this.byTag.get(tag) || new Set();
  }
  
  /**
   * Full-text search - O(k) where k = words in query
   * 
   * Uses inverted index for efficient multi-word search
   */
  search(query: string): Set<string> {
    // Tokenize query
    const words = query
      .toLowerCase()
      .replace(/[^\w\s]/g, ' ')
      .split(/\s+/)
      .filter(w => w.length > 2);
    
    if (words.length === 0) {
      return new Set();
    }
    
    // Get nodes matching first word
    let results = this.wordIndex.get(words[0]) || new Set();
    
    // Intersect with other words (AND logic)
    for (let i = 1; i < words.length; i++) {
      const wordNodes = this.wordIndex.get(words[i]) || new Set();
      results = new Set([...results].filter(id => wordNodes.has(id)));
      
      // Early exit if no matches
      if (results.size === 0) break;
    }
    
    return results;
  }
  
  /**
   * Get all node IDs
   */
  getAllIds(): Set<string> {
    return new Set(this.byId.keys());
  }
  
  /**
   * Get node position in document
   */
  getPosition(nodeId: string): number | null {
    return this.nodePositions.get(nodeId) ?? null;
  }
  
  /**
   * Get node's container ID
   */
  getContainer(nodeId: string): string | null {
    return this.nodeContainers.get(nodeId) || null;
  }
  
  /**
   * Get all nodes in container
   */
  getNodesInContainer(containerId: string): Set<string> {
    const nodeIds = new Set<string>();
    
    for (const [nodeId, container] of this.nodeContainers.entries()) {
      if (container === containerId) {
        nodeIds.add(nodeId);
      }
    }
    
    return nodeIds;
  }
  
  /**
   * Get all entries (for iteration)
   */
  getAllEntries(): Map<string, NodeReference> {
    return this.byId;
  }
  
  // ========================================================================
  // INDEX MAINTENANCE
  // ========================================================================
  
  /**
   * Update index when node added
   */
  addNode(
    id: string,
    element: Y.XmlElement,
    position: number,
    containerId: string
  ): void {
    // Add to ID index
    this.byId.set(id, { element, position, containerId });
    this.nodePositions.set(id, position);
    this.nodeContainers.set(id, containerId);
    
    // Add to className index
    const className = element.getAttribute('className');
    if (className) {
      if (!this.byClassName.has(className)) {
        this.byClassName.set(className, new Set());
      }
      this.byClassName.get(className)!.add(id);
    }
    
    // Add to tag index
    const tags = element.getAttribute('tags');
    if (Array.isArray(tags)) {
      tags.forEach(tag => {
        if (!this.byTag.has(tag)) {
          this.byTag.set(tag, new Set());
        }
        this.byTag.get(tag)!.add(id);
      });
    }
    
    // Add to text index
    const text = this.extractText(element);
    if (text) {
      this.indexText(id, text);
    }
  }
  
  /**
   * Update index when node removed
   */
  removeNode(nodeId: string): void {
    const ref = this.byId.get(nodeId);
    if (!ref) return;
    
    // Remove from ID index
    this.byId.delete(nodeId);
    this.nodePositions.delete(nodeId);
    this.nodeContainers.delete(nodeId);
    
    // Remove from className index
    const className = ref.element.getAttribute('className');
    if (className) {
      this.byClassName.get(className)?.delete(nodeId);
      if (this.byClassName.get(className)?.size === 0) {
        this.byClassName.delete(className);
      }
    }
    
    // Remove from tag index
    const tags = ref.element.getAttribute('tags');
    if (Array.isArray(tags)) {
      tags.forEach(tag => {
        this.byTag.get(tag)?.delete(nodeId);
        if (this.byTag.get(tag)?.size === 0) {
          this.byTag.delete(tag);
        }
      });
    }
    
    // Remove from text index
    const words = this.nodeWords.get(nodeId);
    if (words) {
      words.forEach(word => {
        this.wordIndex.get(word)?.delete(nodeId);
        if (this.wordIndex.get(word)?.size === 0) {
          this.wordIndex.delete(word);
        }
      });
      this.nodeWords.delete(nodeId);
    }
  }
  
  /**
   * Update index when node modified
   */
  updateNode(
    nodeId: string,
    element: Y.XmlElement,
    newAttributes?: Record<string, any>
  ): void {
    // For simplicity, remove and re-add
    // In production, could optimize by only updating changed attributes
    this.removeNode(nodeId);
    
    const ref = this.byId.get(nodeId);
    if (ref) {
      this.addNode(nodeId, element, ref.position, ref.containerId);
    }
  }
  
  /**
   * Clear all indices
   */
  clear(): void {
    this.byId.clear();
    this.byClassName.clear();
    this.byTag.clear();
    this.wordIndex.clear();
    this.nodeWords.clear();
    this.nodePositions.clear();
    this.nodeContainers.clear();
  }
  
  /**
   * Get index statistics
   */
  getStats(): IndexStats {
    return {
      byId: this.byId.size,
      byClass: this.byClassName.size,
      fullText: this.wordIndex.size,
      tags: this.byTag.size,
      buildTime: this.lastBuildTime,
      totalWords: Array.from(this.wordIndex.values())
        .reduce((sum, set) => sum + set.size, 0)
    };
  }
  
  /**
   * Check if node exists in index
   */
  has(nodeId: string): boolean {
    return this.byId.has(nodeId);
  }
  
  /**
   * Get index size
   */
  size(): number {
    return this.byId.size;
  }
}

